/*
 * Copyright © Scale Microgrid Solutions Operating, LLC [2023].
 * All rights reserved.
 *
 * SPDX-FileCopyrightText: ©2023 Scale Microgrid Solutions Operating, LLC <legal@scalemicrogrids.com>
 */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useQuery } from "@tanstack/react-query";
import { useFlags } from "launchdarkly-react-client-sdk";
import { Box, Typography, Button } from "@mui/material";
import PolylineOutlinedIcon from "@mui/icons-material/PolylineOutlined";
import {
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  ReferenceLine,
  ReferenceArea,
  ComposedChart,
} from "recharts";
import {
  getSiteTimeSeriesQuery,
  getSiteAssembliesQuery,
  getAlarmHistoryQuery,
  getWeatherHistoricalQuery,
} from "../../../lib/Queries";
import DropdownEnergy from "../Common/DropdownEnergy";
import LineGraphTooltip from "../Common/LineGraphTooltip";
import CustomLegend from "../Common/CustomLegend";
import Loading from "../../Shared/Loading";
import TimeSeriesError from "../Common/TimeSeriesError";
import DateOptionPicker from "../Common/DateOptionPicker";
import AlarmTimeline from "../Common/AlarmTimeline";
import CustomTick from "../Common/CustomTick";
import CustomXAxis from "../Common/CustomXAxis";
import DownloadDialog from "../Common/DownloadDialog";
import AlarmsDownloadDialog from "../Common/AlarmsDownloadDialog";
import GraphToggle from "../Common/GraphToggle";
import GraphDownloadMenu from "./../Common/GraphDownloadMenu";
import WeatherTimeline from "../Common/WeatherTimeline";
import { assemblyColor, assemblyFillColor } from "../Logic/formatUtils";
import { getUnixTimeMs } from "../../../lib/Util";
import GraphUtil from "../../../lib/GraphUtil";
import { weatherConfig } from "../Common/weatherConfig";
import { generateGetWeatherTooltipItems } from "../../../lib/weatherUtils";

const SitePowerGraph = ({ site }) => {
  const flags = useFlags();
  const [manualRefresh, setManualRefresh] = useState(false);
  const [timeSeries, setTimeSeries] = useState([]);
  const [timezone] = useState(site.timezone);
  const [objKeys, setObjKeys] = useState([]);
  const [areaVisibility, setAreaVisibility] = useState({});
  const [animation, setAnimation] = useState(true);
  const [animationCount, setAnimationCount] = useState(0);
  const [bigBuckets, setBigBuckets] = useState([]);
  const [zoomParams, setZoomParams] = useState({ zoomed: false });
  const [preZoomDisplayRange, setPreZoomDisplayRange] = useState(null);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [useGraphInterpolation, setUseGraphInterpolation] = useState(true);
  const [displayRange, setDisplayRange] = useState(GraphUtil.getInitialGraphRange(timezone));
  const [powerDownloadOpen, setPowerDownloadOpen] = useState(false);
  const [alarmsDownloadOpen, setAlarmsDownloadOpen] = useState(false);
  const [graphPayload, setGraphPayload] = useState(null);

  const refreshGraph = () => {
    const initialRange = GraphUtil.getInitialGraphRange(timezone);
    setDisplayRange(GraphUtil.updateDisplayRange({ ...initialRange }));
    setZoomParams({ zoomed: false, zooming: false });
    setManualRefresh(true);
    // Manually trigger refetches to update immediately
    refetch();
    refetchAlarms();
    refetchAssemblies();
  };

  const updateAnimationState = (animation) => {
    setAnimation(false);
  };

  /**
   * Handles the onAnimationEnd event of the Area components.
   * Increments the animation count and checks if all animations are completed.
   * If all animations are completed, it sets the animation state to false.
   *
   * @returns {void}
   */
  const handleAnimationEnd = () => {
    setAnimationCount((prevCount) => prevCount + 1);
    if (animationCount >= 0) {
      setAnimation(false);
    }
  };

  const {
    isLoading: isSiteTimeSeriesLoading,
    error,
    data: siteTimeSeriesData,
    refetch,
    isRefetchError,
  } = useQuery({
    ...getSiteTimeSeriesQuery(
      site.uuid,
      displayRange.start,
      displayRange.stop,
      displayRange.interval,
    ),
  });

  const { data: weatherTimeSeriesData } = useQuery({
    ...getWeatherHistoricalQuery(site.uuid, displayRange.start, displayRange.stop),
  });

  const {
    isLoading: isAlarmHistoryLoading,
    data: alarmHistoryData,
    refetch: refetchAlarms,
  } = useQuery({
    ...getAlarmHistoryQuery(site.uuid, displayRange.start, displayRange.stop),
  });

  const {
    isLoading: isAssemblyDataLoading,
    isFetching: isAssemblyFetching,
    isFetched: isAssemblyFetched,
    isError: isAssemblyError,
    data: assemblyData,
    refetch: refetchAssemblies,
  } = useQuery({
    ...getSiteAssembliesQuery(site.uuid),
  });

  const isLoading = isSiteTimeSeriesLoading || isAlarmHistoryLoading || isAssemblyDataLoading;
  const hasQueryError = Boolean(error || isRefetchError || isAssemblyError);

  useEffect(() => {
    if (Array.isArray(objKeys) && Object.keys(areaVisibility).length === 0 && objKeys.length > 0) {
      // Step 1: Filter only keys that include a dash
      const dashedKeys = objKeys.filter((key) => key.includes("-"));

      // Step 2: Count occurrences of typePart in the dashed keys
      const typePartCount = dashedKeys.reduce((acc, key) => {
        const [, typePart] = key.split("-");
        acc[typePart] = (acc[typePart] || 0) + 1;
        return acc;
      }, {});

      // Step 3: Build the final list based on the criteria
      // When there are multiple assets of the same type, we want the parent/aggregate
      // to be checked by default (e.g., "BESS" or "Genset"), not the individuals.
      // Individual assets can be toggled via the legend's expand functionality.
      const finalKeys = objKeys.filter((key) => {
        if (key.includes("-")) {
          const [namePart, typePart] = key.split("-");

          if (namePart === typePart) {
            // Include if namePart and typePart are the same (e.g., "BESS-BESS")
            return true;
          } else {
            // For multiple assets of same type: exclude individuals, use parent aggregate
            // For single asset: include the individual
            return typePartCount[typePart] > 1
              ? key === `${typePart}-${typePart}`
              : key === `${namePart}-${typePart}`;
          }
        }
        // Include non-dashed keys (parent/aggregate keys like "BESS", "Genset")
        return true;
      });
      // Step 4: Process finalKeys, removing duplicates but keeping the
      // first valid match
      const keysWithDash = finalKeys.filter((key) => key.includes("-"));
      const keysWithoutDash = finalKeys.filter((key) => !key.includes("-"));
      // Extract the first parts from keys with a dash
      const firstPartsFromDash = keysWithDash.map((key) => key.split("-")[0]);
      // Extract the second parts from keys with a dash
      const secondPartsFromDash = keysWithDash.map((key) => key.split("-")[1]);
      // Create a unique list starting with first parts of dashed keys, followed
      // by those without dashes, excluding those in second parts
      const uniqueKeys = [
        ...new Set(firstPartsFromDash),
        ...keysWithoutDash.filter((key) => !secondPartsFromDash.includes(key)),
      ];
      const initialAreaVisibility = uniqueKeys.reduce((acc, key) => {
        return { ...acc, [key]: true };
      }, {});
      // Alarms should always be visible
      initialAreaVisibility.alarms = true;
      setAreaVisibility(initialAreaVisibility);
    }
  }, [objKeys]);

  useEffect(() => {
    refetch();
    refetchAlarms();
    setManualRefresh(true);
  }, [displayRange]);

  useEffect(() => {
    if (assemblyData) {
      const assembliesObj = assemblyData.flatMap((item) => {
        const { group_assemblies, assemblies } = item;

        // Ensure assemblies is an array before calling .map()
        const assemblyKeys = Array.isArray(assemblies)
          ? assemblies.map((obj) => `${obj.scaleos_name}-${obj.scaleos_type}`)
          : [];

        return [group_assemblies, ...assemblyKeys];
      });

      setObjKeys(Array.from(new Set(assembliesObj)));
    }
  }, [assemblyData]);

  useEffect(() => {
    if (hasQueryError) {
      setManualRefresh(false);
      setInitialLoadComplete(true);
      return;
    }
    // Stay on the outer spinner until a series (or empty soft-miss) arrives.
    if (siteTimeSeriesData === undefined) {
      return;
    }
    const series = siteTimeSeriesData?.time_series;
    if (series?.length > 0 && objKeys.length > 0) {
      // This is to add missing data to the checkboxes if found in the timeseries data
      const hasMissingDataField = series.some((item) =>
        Object.keys(item).some((key) => key.startsWith("missing_data_")),
      );
      if (hasMissingDataField) {
        setAreaVisibility((prev) => ({
          ...prev,
          missingData: true,
        }));
      }
      const seriesData = series.map((item) => ({
        ...item,
        unixTime: getUnixTimeMs(item.bucket, timezone),
      }));
      const { timeSeries: seriesDataWithBigBuckets, newBigBuckets } =
        GraphUtil.calculateBigBuckets(seriesData);
      setBigBuckets(newBigBuckets);
      setTimeSeries(seriesDataWithBigBuckets);
      setManualRefresh(false);
      setInitialLoadComplete(true);
      return;
    }
    if (Array.isArray(series) && series.length === 0) {
      // Soft miss / empty series: keep chrome mounted, clear stale plot + spinner.
      setTimeSeries([]);
      setBigBuckets([]);
      setManualRefresh(false);
      setInitialLoadComplete(true);
      return;
    }
    // Nonempty series but no objKeys: wait only while assemblies are pending.
    if (series?.length > 0 && objKeys.length === 0) {
      if (!isAssemblyFetched || isAssemblyFetching) {
        return;
      }
      // Assemblies settled without keys (soft miss) — still plot the series.
      const seriesData = series.map((item) => ({
        ...item,
        unixTime: getUnixTimeMs(item.bucket, timezone),
      }));
      const { timeSeries: seriesDataWithBigBuckets, newBigBuckets } =
        GraphUtil.calculateBigBuckets(seriesData);
      setBigBuckets(newBigBuckets);
      setTimeSeries(seriesDataWithBigBuckets);
      setManualRefresh(false);
      setInitialLoadComplete(true);
    }
  }, [siteTimeSeriesData, objKeys, hasQueryError, isAssemblyFetched, isAssemblyFetching]);

  const styles = {
    downloadButtonBox: {
      display: "flex",
      flexDirection: "column",
      gap: ".6rem",
    },
  };

  const leftMargin = 20;
  const rightMargin = 20;
  const yAxisWidth = 40;

  const removeAssetNumber = (asset_string) => {
    // Split the string by the dash and return the first part (before the dash)
    return asset_string.includes("-") ? asset_string.split("-")[0] : asset_string;
  };

  const getPayload = () => {
    const payload = objKeys.map((key) => {
      // Split the key to get the part before and after the dash
      const [namePart, typePart] = key.includes("-") ? key.split("-") : [key, null];

      return {
        value: removeAssetNumber(namePart), // Use the part before the dash
        type: typePart,
        color: typePart ? assemblyColor[typePart] : assemblyColor[namePart], // Use the part after the dash for color
      };
    });

    // Filter out entries that do not have typePart
    const filteredPayload = payload.filter((entry) => entry.type !== null);
    return filteredPayload;
  };

  const cancelZoom = () => {
    setZoomParams({ zooming: false, zoomed: false });
    setDisplayRange(GraphUtil.updateDisplayRange(preZoomDisplayRange));
  };

  const getBucketAlarms = GraphUtil.generateGetBucketAlarms(bigBuckets, alarmHistoryData);

  const showAlarms = areaVisibility["alarms"];

  const showMissingData = areaVisibility["missingData"];

  const getWeatherTooltipItems = generateGetWeatherTooltipItems(
    weatherConfig,
    weatherTimeSeriesData,
  );

  const handleWeatherHover = (ev) => {
    if (ev?.activeLabel == null) return;
    const synthetic = GraphUtil.buildSyntheticGraphPayload(
      timeSeries,
      Number(ev.activeLabel),
      (row) =>
        objKeys.flatMap((key) => {
          const [namePart, typePart] = key.includes("-") ? key.split("-") : [key, key];
          if (namePart === typePart && key.includes("-")) return [];
          return [
            {
              name: namePart,
              value: row[namePart],
              color: assemblyColor[typePart],
              payload: row,
            },
          ];
        }),
    );
    if (synthetic) setGraphPayload(synthetic);
  };

  const syncGraphs = (_tooltipTicks, hoverGraphData) => {
    // When hovering over the site power graph, this method is still called (quirk of recharts).  In that case we want to simply return the current index.
    if (hoverGraphData.activeLabel === hoverGraphData.activeTooltipIndex) {
      return hoverGraphData.activeTooltipIndex;
    }
    return GraphUtil.findIndexByClosestUnixTime(timeSeries, Number(hoverGraphData.activeLabel));
  };

  if (!initialLoadComplete)
    return (
      <div className="chart-graph">
        <Box display="flex" flexDirection="row" mt="20px" ml="20px" alignItems="baseline">
          <Box>
            <Typography variant="body2" color="secondary">
              Power
            </Typography>
          </Box>
        </Box>
        <Loading />
      </div>
    );

  if (hasQueryError)
    return (
      <div className="chart-graph">
        <Box display="flex" flexDirection="row" mt="20px" ml="20px" alignItems="baseline">
          <Box>
            <Typography variant="body2" color="secondary">
              Power
            </Typography>
          </Box>
        </Box>
        <TimeSeriesError>
          <Button variant="contained" color="secondary" onClick={refreshGraph}>
            Refresh Graph
          </Button>
        </TimeSeriesError>
      </div>
    );

  if (siteTimeSeriesData) {
    return (
      <div className="chart-graph" style={{ top: "40px" }}>
        <Box
          width="95%"
          display="flex"
          flexDirection="row"
          mt="20px"
          ml="20px"
          alignItems="baseline"
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="body2" color="secondary">
              Power
            </Typography>
          </Box>
        </Box>
        <Box
          width="95%"
          ml="20px"
          pt="30px"
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          mb="2rem"
        >
          <Box display="flex" flexDirection="row">
            <DateOptionPicker
              site={site}
              updateDisplayRange={(range) => {
                setDisplayRange(GraphUtil.updateDisplayRange(range));
                setZoomParams({ zooming: false, zoomed: false });
              }}
              range={displayRange.range}
            />
            <Box sx={{ ml: "10px" }}>
              <GraphToggle
                value={useGraphInterpolation}
                onChange={() => setUseGraphInterpolation(!useGraphInterpolation)}
                title="Interpolation"
                Icon={PolylineOutlinedIcon}
              />
            </Box>
          </Box>
          <Box sx={{ display: "flex" }}>
            <Box mr="10px">
              <DropdownEnergy
                site={site}
                displayRange={displayRange}
                manualRefresh={manualRefresh}
              />
            </Box>
            <GraphDownloadMenu displayRange={displayRange} site={site} timezone={timezone} />
          </Box>
        </Box>
        {/* TODO: Add weather chip back in once we have the data */}
        {/* <Box width="95%" ml="20px" mb="1rem">
          <WeatherChip siteUuid={site.uuid} />
        </Box> */}
        <Box display="flex" flexDirection="row">
          <Box flexGrow="1">
            <Box display="flex" flexDirection="column" width="100%">
              {isLoading || manualRefresh ? (
                <>
                  <Box display="flex" flexDirection="row" mt="20px" ml="20px" alignItems="baseline">
                    <Box>
                      <Typography variant="body2" color="secondary">
                        Power
                      </Typography>
                    </Box>
                  </Box>
                  <Loading />
                </>
              ) : (
                <>
                  {weatherTimeSeriesData && flags.weatherTimeseries && (
                    <WeatherTimeline
                      data={weatherTimeSeriesData}
                      displayRange={displayRange}
                      rightMargin={rightMargin}
                      leftMargin={leftMargin + yAxisWidth}
                      yAxisMargin={yAxisWidth}
                      weatherConfig={weatherConfig}
                      timezone={timezone}
                      onMouseMove={handleWeatherHover}
                      onMouseLeave={() => setGraphPayload(null)}
                    />
                  )}
                  <Box sx={{ width: "100%", height: "400px" }}>
                    <ResponsiveContainer width="99%" height="99%">
                      <ComposedChart
                        syncId="power-graph"
                        data={timeSeries}
                        syncMethod={syncGraphs}
                        margin={{
                          top: 0,
                          right: rightMargin,
                          left: leftMargin,
                          bottom: 0,
                        }}
                        onMouseDown={(ev) =>
                          GraphUtil.handleZoomStart(ev, zoomParams, setZoomParams)
                        }
                        onMouseUp={(ev) => {
                          const newRange = GraphUtil.handleZoomEnd(
                            ev,
                            zoomParams,
                            setZoomParams,
                            timezone,
                            displayRange,
                          );
                          if (newRange) {
                            setPreZoomDisplayRange(displayRange);
                            setDisplayRange(GraphUtil.updateDisplayRange(newRange));
                          }
                        }}
                        onMouseMove={(ev) => {
                          GraphUtil.handleZoomUpdate(ev, zoomParams, setZoomParams);
                          setGraphPayload(ev.activePayload);
                        }}
                        onMouseLeave={() => {
                          setGraphPayload(null);
                        }}
                        width={40}
                      >
                        <CustomXAxis hide={false} />
                        <YAxis
                          unit="kW"
                          tick={<CustomTick unit="kW" />}
                          axisLine={false}
                          style={{
                            fontFamily: "IBM Plex Sans, Arial, Helvetica, sans-serif",
                            fontStyle: "normal",
                            fontWeight: "500",
                            fontSize: "10px",
                            color: "#bebebe",
                          }}
                          width={yAxisWidth}
                        />
                        <Tooltip
                          wrapperStyle={{ zIndex: 10000 }}
                          content={
                            <LineGraphTooltip
                              areaVisibility={areaVisibility}
                              // timeSeries item implicitly passed in from ComposedChart
                              getBucketAlarms={getBucketAlarms}
                              timezone={timezone}
                              showAlarms={showAlarms}
                              graphPayload={graphPayload}
                              colorPaletteKey={"sitePowerGraphColors"}
                              getWeatherTooltipItems={getWeatherTooltipItems}
                            />
                          }
                          filterNull={false}
                        />
                        {objKeys
                          .sort((a, b) => {
                            const desiredOrder = ["BESS", "Solar", "Genset", "Utility", "Load"];

                            const nameA = a.includes("-") ? a.split("-")[1] : a;
                            const nameB = b.includes("-") ? b.split("-")[1] : b;

                            const indexA = desiredOrder.indexOf(nameA);
                            const indexB = desiredOrder.indexOf(nameB);

                            return (
                              (indexA === -1 ? Infinity : indexA) -
                              (indexB === -1 ? Infinity : indexB)
                            );
                          })
                          .map((key, index) => {
                            const [namePart, typePart] = key.includes("-")
                              ? key.split("-")
                              : [key, key];
                            // Skip the entry if namePart and typePart are the same and key includes a dash
                            if (namePart === typePart && key.includes("-")) {
                              return null; // Skip this entry
                            }
                            const isVisible = areaVisibility[namePart];
                            return (
                              <Area
                                isAnimationActive={animation}
                                onAnimationEnd={handleAnimationEnd}
                                key={namePart}
                                type={useGraphInterpolation ? "monotone" : "linear"}
                                stackId={typePart === "Load" ? "2" : "1"}
                                dataKey={namePart}
                                stroke={assemblyColor[typePart]}
                                strokeWidth={2}
                                fillOpacity={typePart === "Load" ? 0 : 1}
                                fill={assemblyFillColor[typePart]}
                                {...(typePart === "Load"
                                  ? { strokeDasharray: "1 1", strokeWidth: 1 }
                                  : {})}
                                hide={!isVisible}
                                connectNulls={useGraphInterpolation ? true : false}
                                style={{ zIndex: 20 }}
                              />
                            );
                          })}
                        <CartesianGrid
                          strokeDasharray="4 4"
                          stroke="#808080"
                          strokeOpacity="10%"
                          vertical={false}
                        />
                        <ReferenceLine
                          y={0}
                          stroke="#325182"
                          strokeDasharray="3 3"
                          isFront={true}
                        />
                        {zoomParams?.zooming ? (
                          <ReferenceArea x1={zoomParams.startIndex} x2={zoomParams.stopIndex} />
                        ) : null}
                      </ComposedChart>
                    </ResponsiveContainer>
                  </Box>
                  <AlarmTimeline
                    timeSeries={timeSeries}
                    displayRange={displayRange}
                    bigBuckets={bigBuckets}
                    alarmHistoryData={alarmHistoryData}
                    yAxisMargin={40}
                    axisOnly={!showAlarms}
                    timezone={timezone}
                    getBucketAlarms={getBucketAlarms}
                    rightMargin={rightMargin}
                    leftMargin={leftMargin + yAxisWidth}
                    showMissingData={showMissingData}
                  />
                </>
              )}
            </Box>
          </Box>
          <Box
            flexBasis="auto"
            pr="2rem"
            flexDirection="column"
            display="flex"
            justifyContent="space-around"
          >
            <CustomLegend
              payload={getPayload()}
              areaVisibility={areaVisibility}
              setAreaVisibility={setAreaVisibility}
              updateAnimationState={updateAnimationState}
            />
            {zoomParams.zoomed ? (
              <Button variant="outlined" size="small" color="alt" onClick={cancelZoom}>
                Cancel Zoom
              </Button>
            ) : null}
          </Box>
        </Box>
        <DownloadDialog
          open={powerDownloadOpen}
          start={displayRange.start}
          stop={displayRange.stop}
          defaultInterval={displayRange.interval}
          onClose={() => setPowerDownloadOpen(false)}
          siteUuid={site.uuid}
          siteName={site.site_name}
        />
        <AlarmsDownloadDialog
          open={alarmsDownloadOpen}
          start={displayRange.start}
          stop={displayRange.stop}
          onClose={() => setAlarmsDownloadOpen(false)}
          siteUuid={site.uuid}
          siteName={site.site_name}
          timezone={timezone}
        />
      </div>
    );
  }
};

SitePowerGraph.propTypes = {
  site: PropTypes.object.isRequired,
};

export default SitePowerGraph;
